/** Типы ответов ecm. Выписаны с живого API локального стенда,
 *  сверены с Product_1c_frontentd/src/api/* и контроллерами Product.Api. */

export interface ProductGroupIdentity {
	id: string
	name: string
	isImageChecked: boolean
	isDescriptionChecked: boolean
	isToolset: boolean
	priceGroupId: number | null
	siteID: number | null
	showStatus: number | null
}

export interface ProductGroup {
	id: string
	name: string
	description: string
	descriptionWeb: string
	attributesColumnOrder: Record<
		string,
		{ isImportant: boolean; importantSort: number | null; sort: number }
	>
	seriesId: number | null
	signId: number | null
	sellmarkId: number | null
	priceGroupId: number | null
	mainAttributeId: number | null
	isToolset: boolean
	isImageChecked: boolean
	isDescriptionChecked: boolean
	siteId: number | null
	wasCreate: boolean
}

export interface ProductIdentity {
	id: string
	name: string
	stockRus: number | null
	isInStatistic: boolean
	priceGroupId: number | null
}

/** Строка табличной части: товар со значениями атрибутов.
 *  products-with-attr/by-groups — ProductWithAttributesExtended. */
export interface ProductRow extends ProductIdentity {
	sort: number | null
	attributeValues: { id: number; value: string }[]
}

/** Стратегия подстановки названия атрибута при сборке короткого имени товара —
 *  `ProductNameAttributeStrategy.cs`. Роль Admin выбирает любую из трёх в ECM
 *  (`TranslateForm.tsx`); без неё ECM всегда шлёт `ByAttributeShortNameAndAttributeName`.
 *  У Риги роли Admin нет, поэтому в интерфейсе выбора тоже нет — всегда это значение. */
export enum ProductNameAttributeStrategy {
	ByAttributeShortName = 0,
	ByAttributeName = 1,
	ByAttributeShortNameAndAttributeName = 2,
}

/** Справочник атрибутов на выбранном языке. */
export interface Attribute {
	id: number
	name: string
}

export interface NamedImage {
	name: string
	extension: string | null
	modifyDate: string
	typeId: number
	imageUrl: string
}

export interface ImageType {
	id: number
	name: string
}

export interface Language {
	id: number
	name: string
	deeplLanguageCode: string | null
}

export interface PriceGroup {
	id: number
	name: string
}

export interface WebsiteIdentity {
	id: number
	name: string
	schema: number
}

/**
 * Ответы рижского BFF (`/ai/site/*`) — всё, что касается базы самого сайта.
 *
 * Отдельно от остальных типов: у этих методов другой бэк. `ecm` в базы сайтов
 * Риги не ходит вовсе — см. `SITE_API_ENABLED` в `api/config.ts`.
 */
export interface GroupSiteStatus {
	websiteId: number
	siteName: string
	groupId: string
	/** `ProductGroups.SiteID` — под ним группа лежит в `oc_product`. */
	siteProductId: number
	isToolSet: boolean
	onSite: boolean
	/** Что предложить нажать: `add` или `remove`. */
	suggestedAction: "add" | "remove"
	/** Сколько строк группы лежит сейчас в каждой таблице сайта. */
	rows: Record<string, number>
	/** Почему группа не поедет на сайт. Пусто — поедет. */
	blockers: string[]
}

/** Отчёт `POST /ai/site/group/add`. `journalId` — по нему возможен откат. */
export interface AddReport {
	journalId: string
	groupId: string
	websiteId: number
	inserted: Record<string, number>
	cleaned: Record<string, number>
	verifiedOnSite: boolean
	/** Сколько строк привязки к каталогу сайта узел создал в ERP перед
	 *  выгрузкой. Ноль — группа уже была в каталоге. */
	attachedToCatalog: number
}

/** Отчёт `POST /ai/site/group/remove`. */
export interface RemoveReport {
	journalId: string
	groupId: string
	websiteId: number
	deleted: Record<string, number>
	verifiedGone: boolean
}

/** Схема сайта из таблицы `WebSites`. Рига работает только с `Latvian`. */
export enum WebsiteSchema {
	Russian = 1,
	Latvian = 2,
	ProductSite = 3,
}

/**
 * Таблица OpenCart на сайте — `WebsiteTable.cs`. Значение уходит в
 * `PUT /api/websites/fill-website-table` полем `table`.
 *
 * Бэк вызывает `FillWebsiteTable(..., shouldClear: true, table)`: таблица сайта
 * сначала **чистится целиком**, потом наполняется из выбранного каталога
 * (`SiteCatalogService.cs:175-198`). Одного метода «залить всё» в API нет —
 * порядок таблиц задаёт фронт.
 */
export enum WebsiteTable {
	OcProduct = 0,
	OcProductAttribute = 1,
	OcAttribute = 2,
	OcProductComplect = 3,
	OcProductDescription = 4,
	OcCategory = 5,
	OcProductProductToGroup = 6,
	OcProductProductToComplect = 7,
	OcOption = 8,
	OcProductGroup = 9,
	OcProductGroupToProduct = 10,
	OcPartners = 11,
	OcProductToCategory = 12,
	OcProductOption = 13,
	OcProductImage = 14,
}

/** Точечное обновление уже загруженного сайта — `PartialRefreshTable.cs`.
 *  В отличие от `fill-website-table` ничего не чистит: сравнивает и правит. */
export enum PartialRefreshTable {
	OcProductWeight = 0,
	OcProductDescription = 1,
	OcProductCategory = 2,
	OcProductCategoryImages = 3,
}

/** Какому дереву принадлежат картинки разделов — `WebCategoryType.cs`.
 *  Нужен только `partial-refresh` с таблицей `OcProductCategoryImages`.
 *  Значение `Rg` совпадает со `Schema = 2` — это и есть сайты Риги. */
export enum WebCategoryType {
	Ru = 1,
	Rg = 2,
	WebShop = 3,
}

/** Почему группа не уедет на сайт — `DeployException.cs`.
 *  Проверки `NameNotTranslated`, `NoDescriptionTranslated` и
 *  `NotCheckedDescription` в бэке закомментированы (`DeployChecker.cs:84-96`),
 *  живыми остались `ShowStatus` и `NotCheckedImage`. */
export enum DeployReason {
	ShowStatus = 0,
	NameNotTranslated = 1,
	NoDescriptionTranslated = 2,
	NotCheckedDescription = 3,
	NotCheckedImage = 4,
}

export interface NotReadyToDeployGroup {
	productGroupId: string
	productGroupSiteId: number
	name: string
	reason: DeployReason
}

/** Строка привязки группы к каталогу — `GET /api/catalogs/group-catalogs`.
 *  Строк столько, сколько у группы категорий в каталоге. `showStatus` —
 *  та самая галка «Выводить в кат.», без которой `add-group` отвечает 422. */
export interface ProductGroupCatalog {
	productGroupId: string
	catalogId: number
	catalogName: string
	catalogCategoryId: number | null
	catalogParentCategoryId: number | null
	webCategoryId: number | null
	sort: number | null
	showStatus: boolean
}

/** Каталог: печатный, сайт или каталог продуктов. Отдаётся `GET /api/catalogs`.
 *  В справочнике переводов каталог задаёт область выборки — какие группы,
 *  атрибуты и категории попадут в список. */
export interface Catalog {
	id: number
	name: string
	isPrinted: boolean
	isProductCatalog: boolean
	/** `CatalogPdfStyle` в `Product.Domain.ERP.Common...Catalog.cs`
	 *  (`Avtodelo = 0, Beril = 1`) — бэк уже отдаёт это поле в `GET /api/catalogs`,
	 *  просто не было объявлено здесь. Определяет визуальный бренд-стиль PDF-каталога
	 *  (`screens/pdf/style.ts`) — фон, логотип, акцентный цвет, папку статики
	 *  пиктограмм/атрибутов/серий (`api/http.ts`, `brandFromCatalogPdfStyle`). */
	catalogPdfStyle: number
}

/** Группа в составе каталога — `GET /ai/catalog/groups`. Рижский BFF, не `ecm`:
 *  обратного метода «группы по каталогу» у Риги в ecm нет (роль не пускает). */
export interface CatalogGroupRow {
	id: string
	name: string | null
	showStatus: boolean
}

/** Товар, из-за которого группа попала в выдачу поиска — `GET /ai/groups/by-product`.
 *  Рижский BFF, не `ecm`: там поиск групп идёт только по названию самой группы. */
export interface GroupProductHit {
	groupId: string
	productId: string
	productName: string | null
}

/** Ответ `POST /ai/catalog/create-printed`. */
export interface CreatedCatalog {
	id: number
	name: string
}

/** Ответ `POST /ai/catalog/create-site`: оба id — и сайта, и его парного каталога. */
export interface CreatedSite {
	websiteId: number
	catalogId: number
	name: string
}

/** Тело `POST /ai/catalog/create-site` — все поля обязательны, это ровно те
 *  же данные, что сейчас заполнены у существующих сайтов в `WebSites`. */
export interface NewSiteFields {
	siteName: string
	serverIp: string
	port: string
	dbName: string
	dbUser: string
	dbPassword: string
	ftpHost: string
	ftpPath: string
	ftpUser: string
	ftpPassword: string
}

/** Раздел справочника переводов — `TranslateSource.cs`.
 *  Это имя исходной таблицы: по нему бэк понимает, что за объект переводится,
 *  и в его пределах разносит перевод по одинаковым русским фразам. */
export enum TranslateSource {
	All = 0,
	AttrNames = 1,
	CatKat = 2,
	CatWeb = 3,
	GroupName = 4,
	PictGroup = 5,
	PictGroupDesc = 6,
	ValueDescription = 7,
}

/** Строка справочника переводов. `id` — идентификатор строки выборки,
 *  уникален внутри ответа; `translateId` — идентификатор фразы, он и уходит
 *  в запись вместе с `sourceId` и `source`.
 *
 *  `additionalInfo` бэк заполняет только для раздела «Названия атрибутов»
 *  (`TranslateReader.cs:189-204`): короткое название и единица измерения
 *  на выбранном языке. */
export interface TranslateRow {
	id: number
	translateId: number
	russian: string
	translate: string
	source: TranslateSource
	sourceId: string
	additionalInfo: Record<string, string> | null
}

/** Строка `GET /api/translate/with-urls` — то, ради чего в старом фронте
 *  существовала отдельная «Форма переводчика».
 *
 *  Отличий от `TranslateRow` два: `url` — адрес товара на выбранном сайте
 *  (только у разделов `GroupName`, `ValueDescription`, `PictGroupDesc`, у
 *  остальных всегда `null`), и `symbolCount` — символы русского текста без
 *  пробелов. `additionalInfo` этот метод не заполняет вовсе. */
export interface TranslateWithUrl {
	id: number
	translateId: number
	russian: string
	translate: string
	source: TranslateSource
	sourceId: string
	url: string | null
	symbolCount: number
}

/** Ответ переводчика: `POST /ai/translate` (рижский BFF). */
export interface TranslatedByMachine {
	original: string
	translated: string
}

/** Узел дерева веб-категорий. Бэк собирает дерево сам и корень в ответ не кладёт:
 *  для Web (`catalogGroup = 0`) это `2000`, для Web_Latvia — `4000`
 *  (`CategoryTreeProvider.cs:24-40`). Названия приходят на запрошенном языке. */
export interface WebCategory {
	id: number
	parentId: number
	name: string
	imageUrl: Record<string, string> | null
	children: WebCategory[]
}

/**
 * Пиктограмма — значок свойства товара (сталь, покрытие, назначение). Один
 * справочник на всю ERP, 68 штук; группе их назначают списком.
 *
 * `imageUrl` — имя файла (`45-steel.png`), а не путь: папка у пиктограмм своя,
 * её знает `pictogramUrl` в `api/http.ts`. `isSetGroup` — пиктограмма для
 * наборов инструментов, у Риги наборов нет.
 */
export interface Pictogram {
	id: number
	name: string
	imageUrl: string
	sort: number
	isSetGroup: boolean
}

/** Категория группы на сайте. `categoryPath` — путь **от листа к верхнему уровню**
 *  (`TreeExtensions.GetPathToParent`), то есть сама категория идёт первой.
 *  `mainCategory` — та единственная, в которой товар живёт на сайте. */
export interface GroupCategory {
	id: number
	productGroupId: string
	categoryPath: number[]
	mainCategory: boolean | null
}

/**
 * Данные для генерации каталога — `GET /api/product-group/guide`.
 *
 * Один ответ = один каталог на одном языке: разделы каталога → категории →
 * товарные группы → товары (и, у наборов, состав по разделам-пиктограммам).
 * Тексты уже разрешены под запрошенный `languageId` на бэке
 * (`u.NameJson.ContainsKey(languageId) ? u.NameJson[languageId] : null`,
 * `GroupReader.cs:401-402`) — перевода нет — поле `null`, отдельного
 * признака или запасного языка бэк не даёт (см. `pdf/coverage.ts`).
 *
 * Картинки идут в двух вариантах на поле: `path` — веб-качество (то же, что
 * отдаёт `/api/images`), `convertImageUrl` — конвертированный печатный вариант
 * (CMYK/SVG для типографии). Страница генерации каталогов Риги — веб-качество,
 * поэтому используется `path`; `convertImageUrl` у товарных фото не берём.
 * У пиктограмм, картинок разделов, серий и значков веб-варианта нет вовсе —
 * там всегда только `convertImageUrl` (векторные значки, не фото).
 */
export interface Guide {
	/** Верхний уровень разделов каталога — из них строится оглавление PDF
	 *  (один `<Page bookmark>` на раздел, `pdf/CatalogDocument.tsx`). */
	parentCategories: GuideCatalogRef[]
	/** Второй уровень — категории с товарными группами внутри. `parentId`
	 *  указывает на `parentCategories`; категория без совпадения — «Без раздела». */
	data: GuideCategory[]
	/** Справочник пиктограмм каталога — значки под карточкой товара/набора. */
	pictograms: GuidePictogram[]
	/** Справочник атрибутов — по нему подписываются колонки таблицы товаров. */
	attributes: GuideAttribute[]
	series: GuideImageRef[]
	signs: GuideImageRef[]
}

export interface GuideCatalogRef {
	id: number
	name: string | null
	convertImageUrl: string | null
}

export interface GuideCategory extends GuideCatalogRef {
	parentId: number | null
	groups: GuideGroup[]
}

export interface GuideGroupImage {
	typeId: number
	/** Веб-вариант — используем его, см. заголовок раздела. */
	path: string | null
	ratio: number | null
	convertImageUrl: string | null
}

export interface GuideAttributeValue {
	attributeId: number
	value: string | null
}

export interface GuideProduct {
	id: string
	name: string | null
	/** Не заполняется бэком (`GroupReader.cs:515-524` не пишет это поле) —
	 *  оставлено для точности типа, использовать нельзя. */
	pictogramId: string | null
	attributes: GuideAttributeValue[]
}

/** Товар-компонент набора. `attributes` — значения без привязки к
 *  attributeId (порядок как в `GroupForPdf.attributes` группы-набора). */
export interface GuideToolsetProduct {
	id: string
	name: string | null
	signId: number | null
	mainAttributeId: number | null
	attributes: (string | null)[]
}

export interface GuideGroup {
	id: string
	name: string | null
	description: string | null
	isToolset: boolean
	mainAttributeId: number | null
	urlQr: string | null
	seriesId: number | null
	pictograms: number[]
	/** Id атрибутов колонками таблицы — уже в порядке `attributesColumnOrder`. */
	attributes: number[]
	catalog: GuideCatalogRef | null
	products: GuideProduct[]
	/** Ключ словаря — id пиктограммы-раздела набора. */
	toolsetProducts: Record<number, GuideToolsetProduct[]>
	images: GuideGroupImage[]
}

export interface GuidePictogram {
	id: number
	name: string | null
	imageUrl: string | null
	convertImageUrl: string | null
}

export interface GuideAttribute {
	id: number
	name: string | null
	unit: string | null
}

export interface GuideImageRef {
	id: number
	convertImageUrl: string | null
}

/**
 * Состав набора (комплектация) — строка `GET /api/toolsets`.
 *
 * У карточки-набора один товар (`isToolset = true` у группы), а этот товар и
 * есть «набор» в смысле `ToolsetProductsController` — `toolsetId` в запросах
 * это его `id`. Состав — список товаров-компонентов, каждый со своим разделом
 * (пиктограммой) и позицией; `sort` идёт подряд с 1, бэк сам перенумеровывает
 * остальных при переносе (`ToolsetSorter`, аналог `moveProductSort`).
 */
export interface ToolsetProduct {
	productId: string
	name: string
	sort: number
	pictogramId: number | null
	pictogramName: string | null
}

/**
 * Кандидат в состав набора — `GET /api/toolsets/get-possible-toolset-products`.
 *
 * Список не привязан к прайс-группе: компонентом набора может быть любой товар
 * ERP, кроме уже добавленных и самого набора (`ToolsetReader.cs:19-38`). Бэк
 * заполняет только `Id` и `Name` (общий `ProductIdentity`, остальные поля —
 * `stockRus`/`priceGroupId` пустые, `isInStatistic` всегда `false`), поэтому
 * здесь отдельный, более честный тип, а не переиспользование `ProductIdentity`.
 */
export interface ToolsetCandidate {
	id: string
	name: string
}
